<template>
  <div class="price-data-container">
    <div
      v-show="isLoading"
      class="loading-spinner-container"
    >
      <i class="fad fa-fw fa-spinner-third fa-spin"></i>
    </div>
    <p
      v-if="!isLoading && !csvFormatHistoricalPriceData"
      class="error-message"
    >Historical price data was not found for the symbol: {{ symbol }}</p>
    <div
      v-show="!isLoading && csvFormatHistoricalPriceData"
      class="price-data-container-inner"
    >
      <div class="price-data-header-ui">
        <div>
          <label for="price-data-period-menu" class="sc3-form-label sc3-form-label-inline sc3-form-label-small">PERIOD</label>
          <br />
          <select
            id="price-data-period-menu"
            class="sc3-select-menu sc3-select-menu-inline"
            v-model="period"
          >
            <option
              v-for="periodOption in HISTORICAL_PRICE_DATA_PERIOD_OPTIONS_ARRAY"
              :key="periodOption.value"
              :value="periodOption"
            >{{ periodOption.title }}</option>
          </select>
        </div>
        <div>
          <a
            class="sc3-button sc3-button-grey sc3-button-small"
            :href="`/h-hd/?${chartStore.urlEncodedSymbol}`"
            target="_blank"
          >
            View All Historical Data&nbsp;
            <i class="fas fa-fw fa-arrow-circle-right"></i>
          </a>
        </div>
      </div>
      <div class="price-data">
        <p>{{ csvFormatHistoricalPriceData }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch, onMounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useModes } from '@/composables/useModes';
  import { useModals } from '@/composables/useModals';
  import { fetchHistoricalPriceData } from '@/composables/fetchHistoricalPriceData';
  import { DEFAULT_HISTORICAL_PRICE_DATA_PERIOD_OPTION, HISTORICAL_PRICE_DATA_PERIOD_OPTIONS_ARRAY, HistoricalPriceDataPeriodOption } from '@/enums/historical-price-data-period-options';

  const chartStore = useChartStore();
  const { symbol } = storeToRefs(chartStore);
  const { isPro } = useAuthentication();
  const { HISTORICAL_PRICE_DATA_MODE, currentModeId } = useModes();
  const { launchUpgradeToProModal } = useModals();

  const period = ref<HistoricalPriceDataPeriodOption>(DEFAULT_HISTORICAL_PRICE_DATA_PERIOD_OPTION);
  let loadedPeriod = period.value;
  const isLoading = ref(false);
  const csvFormatHistoricalPriceData = ref('');

  async function loadHistoricalPriceData() {
    if (currentModeId.value !== HISTORICAL_PRICE_DATA_MODE.id) {
      return;
    }

    const currentSymbol = symbol.value;
    const currentPeriod = period.value.value;

    isLoading.value = true;
    csvFormatHistoricalPriceData.value = '';
    let historicalPriceData = '';

    try {
      historicalPriceData = await fetchHistoricalPriceData(currentSymbol, currentPeriod);
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Error while loading historical price data for symbol: ${currentSymbol} and period: ${currentPeriod}.`, e);
    }

    if (
      currentSymbol === symbol.value &&
      currentPeriod === period.value.value
    ) {
      csvFormatHistoricalPriceData.value = historicalPriceData;
      isLoading.value = false;
    }
  }

  async function handleHistoricalPricePeriodChange(newPeriod: HistoricalPriceDataPeriodOption, oldPeriod: HistoricalPriceDataPeriodOption) {
    if (newPeriod.value === loadedPeriod.value) {
      return;
    }

    if (newPeriod.isProOnly && !isPro.value) {
      period.value = oldPeriod;

      launchUpgradeToProModal();
      return;
    }

    loadedPeriod = newPeriod;

    await loadHistoricalPriceData();
  }

  onMounted(() => {
    watch(symbol, loadHistoricalPriceData);
    watch(period, handleHistoricalPricePeriodChange);
    watch(currentModeId, loadHistoricalPriceData);
  });
</script>

<style scoped>
  .price-data-container {
    margin: 0px;
    text-align: center;
  }

  .loading-spinner-container {
    text-align: center;
    font-size: 36px;
    padding: 12px;
  }

  .error-message {
    font-size: 16px;
    text-align: center;
    font-weight: 700;
    margin: 0px;
    padding: 24px 10px;
  }

  .price-data-container-inner {
    margin: 6px;
  }

  .price-data-header-ui {
    display: flex;
    justify-content: space-between;
    align-items: end;
    max-width: 599px; /* TODO: I don't think this will ever change, but still get this programmatically */
    margin: 0 auto;
    text-align: left;
    padding: 0px 0px 8px 0px;
  }

  .price-data {
    overflow: auto;
    max-height: 500px;
  }

  .price-data p {
    display: inline-block;
    background-color: white;
    word-break: break-all;
    word-wrap: break-word;
    white-space: pre;
    font-family: menlo, monospace;
    font-size: 14px;
    font-weight: 500;
    margin: 0px;
    padding: 8px 4px 6px 12px;
  }
</style>
